import type {
  ContentLanguage,
  MarqueeTranslations,
  RestaurantAnnouncement,
} from '@/types';

/** DF-0428 — langue de contenu saisissable par le restaurant, ou null pour le
 *  français (le texte de base). Même règle que `localizeCatalog`. */
function contentLanguage(lang: string): ContentLanguage | null {
  const code = (lang || 'fr').toLowerCase().split('-')[0];
  return code === 'en' || code === 'zh' || code === 'ja' || code === 'ar' ? code : null;
}

/** Messages du bandeau défilant dans la langue de l'interface. Les traductions
 *  sont alignées par position ; une case vide ou absente garde le message
 *  français, le bandeau n'est donc jamais plus court que l'original. */
export function localizedMarquee(
  messages: string[],
  translations: MarqueeTranslations | undefined,
  lang: string,
): string[] {
  const code = contentLanguage(lang);
  const list = code ? translations?.[code] : undefined;
  if (!list) return messages;
  return messages.map((message, i) => list[i]?.trim() || message);
}

function plainTextToHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
}

/** Popup d'accueil dans la langue de l'interface. Le corps traduit est du
 *  texte simple : il remplace le corps riche français, échappé, sauts de
 *  ligne conservés. Un champ non traduit garde le français. */
export function localizedAnnouncement(
  announcement: RestaurantAnnouncement,
  lang: string,
): RestaurantAnnouncement {
  const code = contentLanguage(lang);
  const entry = code ? announcement.translations?.[code] : undefined;
  if (!entry) return announcement;
  const headline = entry.headline?.trim();
  const body = entry.body?.trim();
  const ctaLabel = entry.cta_label?.trim();
  return {
    ...announcement,
    headline: headline || announcement.headline,
    lead: body ? plainTextToHtml(body) : announcement.lead,
    ctaLabel: ctaLabel || announcement.ctaLabel,
  };
}
